// The optimistic-write reconciliation engine (Doc 03 §1, §5). Pure functions over
// a generic slice so every store reconciles the same way and is unit-testable
// without a backend. The flow: applyOptimistic (show instantly) → the repo write
// resolves (ackWrite on success, rollback on failure) → the authoritative stream
// delivers onSnapshot, which converges local state and drops settled deltas.
import { deepEqual } from "@pack-it-up/shared";
export const emptyReconcile = () => ({ byId: new Map(), pending: [] });
const setOrDelete = (m, id, value) => {
    if (value === undefined)
        m.delete(id);
    else
        m.set(id, value);
};
export const applyOptimistic = (s, delta) => {
    const byId = new Map(s.byId);
    setOrDelete(byId, delta.id, delta.after);
    // The spread + acked is correct at runtime; the cast just re-narrows to the union
    // (spreading a distributed union widens `before?/after?` back to optional).
    return { byId, pending: [...s.pending, { ...delta, acked: false }] };
};
// Mark a delta's write as resolved-Ok. It stays in `pending` (so onSnapshot knows
// the write landed and the authoritative value wins) but is *not* removed here —
// the next onSnapshot drops every acked delta, so `pending` is bounded by the number
// of writes still in flight, never growing without bound.
export const ackWrite = (s, deltaId) => ({
    byId: s.byId,
    pending: s.pending.map((d) => (d.deltaId === deltaId ? { ...d, acked: true } : d)),
});
// A failed write: restore the row to its pre-delta value and forget the delta.
export const rollback = (s, deltaId) => {
    const delta = s.pending.find((d) => d.deltaId === deltaId);
    if (!delta)
        return s;
    const byId = new Map(s.byId);
    setOrDelete(byId, delta.id, delta.before);
    return { byId, pending: s.pending.filter((d) => d.deltaId !== deltaId) };
};
// Authoritative snapshot arrives: rebuild from it, then re-apply in-flight (un-acked
// and not-yet-reflected) deltas on top. Confirmed deltas (authoritative already
// equals the optimistic value) and acked-but-diverged deltas (LWW: accept the
// authoritative value) are dropped — calmly, with no error.
export const onSnapshot = (s, authoritative, idOf) => {
    const byId = new Map(authoritative.map((t) => [idOf(t), t]));
    const kept = [];
    for (const d of s.pending) {
        const authVal = byId.get(d.id);
        if (deepEqual(authVal, d.after))
            continue; // confirmed by data
        if (d.acked)
            continue; // write landed; authoritative wins (diverge/LWW)
        setOrDelete(byId, d.id, d.after); // still in flight — keep showing the optimistic value
        kept.push(d);
    }
    return { byId, pending: kept };
};
export const list = (s) => [...s.byId.values()];
export const get = (s, id) => s.byId.get(id);
export const hasPending = (s) => s.pending.length > 0;
//# sourceMappingURL=reconcile.js.map